import React, { useEffect, useRef, useState } from "react";
import { Instagram, Play, ArrowUpRight, MoveHorizontal } from "lucide-react";
import { api, CLINIC, INSTAGRAM_REELS } from "../api";
import { Reveal } from "./motion";

function ReelCard({ code, index, dragRef }) {
    return (
        <a href={`https://www.instagram.com/reel/${code}/`} target="_blank" rel="noopener noreferrer"
            data-testid={`instagram-reel-${index}`}
            onClick={(e) => { if (dragRef.current) e.preventDefault(); }}
            className="group relative mx-3 block w-[280px] shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-navy shadow-sm transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:border-accent/60 select-none"
            draggable={false}>
            <div className="pointer-events-none h-[420px] w-full overflow-hidden">
                <iframe
                    title={`Instagram reel ${index + 1}`}
                    src={`https://www.instagram.com/reel/${code}/embed`}
                    className="h-full w-full border-0"
                    loading="lazy"
                    scrolling="no"
                />
            </div>
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-navy via-navy/70 to-transparent px-4 pb-3 pt-10">
                <span className="font-mono-label text-[0.6rem] text-white/90">@uro_bytes</span>
                <Play className="h-4 w-4 text-white" aria-hidden="true" />
            </div>
        </a>
    );
}

function AutoScroller({ children, speed = 0.12, dragRef }) {
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        let raf, last = performance.now(), paused = false, resumeTimer;
        let down = false, startX = 0, startScroll = 0;

        const step = (t) => {
            const dt = Math.min(t - last, 50);
            last = t;
            if (!paused && el.scrollWidth > el.clientWidth) {
                el.scrollLeft += speed * dt;
                if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft -= el.scrollWidth / 2;
            }
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);

        const pauseTemp = () => {
            paused = true;
            clearTimeout(resumeTimer);
            resumeTimer = setTimeout(() => { paused = false; }, 2200);
        };
        const onDown = (e) => {
            down = true; dragRef.current = false;
            startX = e.clientX; startScroll = el.scrollLeft;
            el.style.cursor = "grabbing";
            pauseTemp();
        };
        const onMove = (e) => {
            if (!down) return;
            const dx = e.clientX - startX;
            if (Math.abs(dx) > 6) dragRef.current = true;
            el.scrollLeft = startScroll - dx;
            pauseTemp();
        };
        const onUp = () => {
            down = false;
            el.style.cursor = "grab";
            pauseTemp();
            setTimeout(() => { dragRef.current = false; }, 50);
        };
        const onWheel = () => pauseTemp();
        const onTouch = () => pauseTemp();

        el.addEventListener("pointerdown", onDown);
        el.addEventListener("pointermove", onMove);
        el.addEventListener("pointerup", onUp);
        el.addEventListener("pointerleave", onUp);
        el.addEventListener("wheel", onWheel, { passive: true });
        el.addEventListener("touchstart", onTouch, { passive: true });

        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(resumeTimer);
            el.removeEventListener("pointerdown", onDown);
            el.removeEventListener("pointermove", onMove);
            el.removeEventListener("pointerup", onUp);
            el.removeEventListener("pointerleave", onUp);
            el.removeEventListener("wheel", onWheel);
            el.removeEventListener("touchstart", onTouch);
        };
    }, [speed, children]);

    return (
        <div ref={ref} className="no-scrollbar flex cursor-grab overflow-x-auto pb-2" data-testid="reels-scroller">
            {children}
            {children}
        </div>
    );
}

export default function InstagramFeed() {
    const [reels, setReels] = useState(INSTAGRAM_REELS);
    const dragRef = useRef(false);

    useEffect(() => {
        api.get("/settings/reels")
            .then((r) => { if (r.data.reels?.length) setReels(r.data.reels); })
            .catch(() => {});
    }, []);

    return (
        <section className="overflow-hidden border-b border-border bg-navy py-20 lg:py-24" data-testid="instagram-feed-section">
            <div className="mx-auto max-w-7xl px-5 lg:px-10">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <Reveal>
                        <p className="font-mono-label text-accent">Instagram</p>
                        <h2 className="mt-4 font-display text-3xl font-medium leading-tight text-white sm:text-4xl">
                            Follow for Urology Education
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1} className="flex items-center gap-4">
                        <span className="hidden items-center gap-2 text-xs text-slate-400 sm:flex" aria-hidden="true">
                            <MoveHorizontal className="h-4 w-4" /> drag or swipe
                        </span>
                        <a href={CLINIC.instagram} target="_blank" rel="noopener noreferrer" data-testid="instagram-follow-btn"
                            className="group inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:border-accent hover:bg-accent">
                            <Instagram className="h-4 w-4" aria-hidden="true" />
                            @uro_bytes
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                    </Reveal>
                </div>
            </div>
            <Reveal delay={0.15} className="mt-12">
                <AutoScroller speed={0.12} dragRef={dragRef}>
                    {reels.map((code, i) => <ReelCard key={code} code={code} index={i} dragRef={dragRef} />)}
                </AutoScroller>
            </Reveal>
        </section>
    );
}
